/**
 * Area documents — PURE view mapping (PR-ONB-DOCS-2). The FE MIRRORS the
 * server-side verdicts (state + canUpload) — it never re-derives policy.
 */

import type { AreaDocumentState, AreaFailureKind } from '@/lib/areaApi';

// ─── Type labels (mirror of the wizard's DocumentUploadStep copy) ───────────

const TYPE_LABELS: Record<string, { label: string; description: string }> = {
  VISURA_CAMERALE: {
    label: 'Visura camerale',
    description: 'Estratto del registro imprese (max 6 mesi).',
  },
  RC_PROFESSIONALE: {
    label: 'Polizza RC professionale',
    description:
      'Copia della polizza in corso di validità (obbligatoria per agenzie e broker).',
  },
};

export function documentTypeView(documentType: string): {
  label: string;
  description: string;
} {
  return (
    TYPE_LABELS[documentType] ?? {
      label: documentType.replaceAll('_', ' ').toLowerCase(),
      description: '',
    }
  );
}

// ─── State badges (lock UI vocabulary) ───────────────────────────────────────

export interface AreaDocumentStateView {
  label: string;
  tone: 'info' | 'success' | 'warning' | 'danger' | 'neutral';
}

const STATE_VIEWS: Record<AreaDocumentState, AreaDocumentStateView> = {
  MISSING: { label: 'Da caricare', tone: 'warning' },
  UPLOADED: { label: 'Caricato', tone: 'info' },
  IN_REVIEW: { label: 'In verifica', tone: 'info' },
  NEEDS_CORRECTION: { label: 'Da correggere', tone: 'warning' },
  VALIDATED: { label: 'Approvato', tone: 'success' },
  REJECTED: { label: 'Respinto', tone: 'danger' },
};

export function documentStateView(state: AreaDocumentState): AreaDocumentStateView {
  return STATE_VIEWS[state];
}

/** Optional documents that were never uploaded read as neutral, not warning. */
export function missingTone(required: boolean): AreaDocumentStateView {
  return required
    ? STATE_VIEWS.MISSING
    : { label: 'Facoltativo', tone: 'neutral' };
}

// ─── Upload error copy (401/403/409/413/415/429/503 handling) ───────────────

export function documentUploadErrorMessage(kind: AreaFailureKind): string {
  switch (kind) {
    case 'AUTH_INVALID':
      return 'Sessione scaduta: accedi di nuovo per continuare.';
    case 'ACCOUNT_NOT_FOUND':
    case 'ACCOUNT_DISABLED':
    case 'ACCOUNT_CONVERTED':
      return 'Account non abilitato per questa operazione.';
    case 'DOC_TYPE_NOT_ALLOWED':
      return 'Questo tipo di documento non si carica da qui: identità e codice fiscale sono coperti dalla verifica d\'identità.';
    case 'DOC_UPLOAD_NOT_ALLOWED':
      return 'I documenti non sono modificabili nello stato attuale della richiesta.';
    case 'DOC_NOT_UPLOADED':
      return 'Il file non risulta ancora caricato: riprova l\'invio.';
    case 'NOT_FOUND':
      return 'Documento non trovato. Aggiorna la pagina e riprova.';
    case 'PAYLOAD_TOO_LARGE':
      return 'File troppo grande: il limite è 10 MB.';
    case 'RATE_LIMITED':
      return 'Troppe richieste: attendi un minuto e riprova.';
    case 'RESUMABLE_DISABLED':
      return 'L\'area onboarding non è al momento disponibile. Riprova più tardi.';
    case 'NETWORK':
      return 'Connessione assente. Controlla la rete e riprova.';
    default:
      return 'Operazione non riuscita. Riprova tra qualche istante.';
  }
}

// ─── Client-side pre-checks (mirror of the BE Zod limits, friendlier UX) ────

export const AREA_DOCUMENT_ALLOWED_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/png',
] as const;
export const AREA_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export function validateFileForUpload(file: {
  type: string;
  size: number;
}): string | null {
  if (!(AREA_DOCUMENT_ALLOWED_MIME as readonly string[]).includes(file.type)) {
    return 'Formato non supportato: carica un PDF o un\'immagine JPG/PNG.'; // 415 equivalente
  }
  if (file.size > AREA_DOCUMENT_MAX_BYTES) {
    return 'File troppo grande: il limite è 10 MB.'; // 413 equivalente
  }
  if (file.size === 0) {
    return 'Il file selezionato è vuoto.';
  }
  return null;
}

// ─── Empty-state truth (review fix #2) ───────────────────────────────────────
//
// "We don't know the required set yet" (org data incomplete in the draft)
// is a different message from "nothing is required for this profile".

export type DocumentsEmptyState =
  | { kind: 'UNRESOLVED'; title: string; message: string }
  | { kind: 'NONE_REQUIRED'; title: string; message: string }
  | { kind: 'LIST' };

export function documentsEmptyState(
  requirementsResolved: boolean,
  itemsCount: number,
): DocumentsEmptyState {
  if (!requirementsResolved && itemsCount === 0) {
    return {
      kind: 'UNRESOLVED',
      title: 'Documenti da definire',
      message:
        'Completa i dati dell\'organizzazione nella sezione Dati per conoscere i documenti richiesti.',
    };
  }
  if (requirementsResolved && itemsCount === 0) {
    return {
      kind: 'NONE_REQUIRED',
      title: 'Nessun documento richiesto',
      message:
        'Per il tuo profilo non sono previsti caricamenti manuali: identità e codice fiscale sono coperti dalla verifica d\'identità.',
    };
  }
  return { kind: 'LIST' };
}

export function formatSizeLabel(sizeBytes: number): string {
  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
}
